import { Form } from "antd"

import type { PermissionRef } from "../../helpers/layout.ts"
import type { PermissionsFlat, SectionRecord } from "../../types.ts"

type PermissionPath = Pick<PermissionRef, "sectionKey" | "subKey" | "actionKey">

const servedValue = (sections: PermissionsFlat, path: PermissionPath) =>
  (sections as Record<string, SectionRecord | undefined>)[path.sectionKey]?.[
    path.subKey
  ]?.[path.actionKey] === true

/**
 * Whether each right is on as the form holds it now, falling back to the
 * served value until its field registers. A panel can show rights stored
 * under several sections, so this reads them wherever they live.
 */
export const useWatchedPermissions = (
  paths: PermissionPath[],
  sections: PermissionsFlat
): boolean[] => {
  const watched = Form.useWatch(
    (values: Record<string, SectionRecord | undefined> | undefined) =>
      paths.map(
        (path) =>
          values?.[path.sectionKey]?.[path.subKey]?.[path.actionKey] ??
          servedValue(sections, path)
      )
  )
  return paths.map((path, index) =>
    watched?.[index] === undefined
      ? servedValue(sections, path)
      : watched[index] === true
  )
}

/** Splits rights by the section and row that store them. */
export const byStoredRow = <
  T extends Pick<PermissionRef, "sectionKey" | "subKey">
>(
  items: T[]
) => {
  const groups = new Map<
    string,
    { sectionKey: string; subKey: string; items: T[] }
  >()
  items.forEach((item) => {
    const id = `${item.sectionKey}.${item.subKey}`
    const group = groups.get(id) ?? {
      sectionKey: item.sectionKey,
      subKey: item.subKey,
      items: []
    }
    group.items.push(item)
    groups.set(id, group)
  })
  return [...groups.values()]
}
